import { useQuery } from '@tanstack/vue-query'
import { computed } from 'vue'
import type { ComputedRef } from 'vue'

import { fetchRouteOptions } from '@/services/routePlanner'
import type { PlannerPoint } from '@/services/routePlanner'
import { WorkerUnauthorizedError } from '@/services/workerApi'

import { useWorkerAuth } from './useWorkerAuth'

/**
 * A→B itineraries.
 *
 * Deliberately **not polled**, unlike the vehicle queries. A journey plan does
 * not change second to second, and this hits a third-party service — repeating
 * the request on a timer would be pure load for no user benefit. It refetches
 * only when the endpoints change.
 */
export function useRouteOptionsQuery(
  start: ComputedRef<PlannerPoint | undefined>,
  end: ComputedRef<PlannerPoint | undefined>,
) {
  const { session, clearSession } = useWorkerAuth()
  const enabled = computed(
    () => start.value !== undefined && end.value !== undefined && session.value !== null,
  )

  return useQuery({
    queryKey: computed(() => ['route-options', start.value, end.value] as const),
    queryFn: async ({ signal }) => {
      if (!start.value || !end.value || !session.value) return []
      try {
        return await fetchRouteOptions(start.value, end.value, { signal, session: session.value })
      } catch (error) {
        if (error instanceof WorkerUnauthorizedError) clearSession()
        throw error
      }
    },
    enabled,
    retry: false,
    staleTime: 5 * 60_000,
  })
}
